import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReplyForm from "./ReplyForm";
import { fetchReplies, deleteReply, deleteComment, editComment, editReply } from "../../../utils/utils";
import { useUser } from "../../../context/UserContext";
import AuthRequiredModal from "../../AlertComponents/AuthRequiredModal";
import { theme } from "../../../theme/colors";

// Small helper: safely pull an id out of various shapes
const extractId = (v) => {
    if (!v) return null;
    if (typeof v === "string" || typeof v === "number") return v;
    if (typeof v === "object") {
        return v.id ?? v.user_id ?? v.userId ?? v.uuid ?? v.pk ?? null;
    }
    return null;
};

const PencilIcon = ({ className = "h-5 w-5" }) => (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M13.586 3.586a2 2 0 112.828 2.828l-8.5 8.5a1 1 0 01-.464.263l-3 .857a.5.5 0 01-.617-.617l.857-3a1 1 0 01.263-.464l8.5-8.5zM12.172 5L4.5 12.672V15.5h2.828L15 7.828 12.172 5z" />
    </svg>
);

const TrashIcon = ({ className = "h-5 w-5" }) => (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M8.75 1a.75.75 0 00-.75.75V3H4.75a.75.75 0 000 1.5h.32l.66 11.2A2.25 2.25 0 007.98 18h4.04a2.25 2.25 0 002.25-2.3l.66-11.2h.32a.75.75 0 000-1.5H12V1.75a.75.75 0 00-.75-.75h-2.5zM10 3H9.5V2.5h1V3H10zm-1.75 4.5a.75.75 0 011.5 0v6a.75.75 0 01-1.5 0v-6zm4 0a.75.75 0 00-1.5 0v6a.75.75 0 001.5 0v-6z" clipRule="evenodd" />
    </svg>
);

const CommentCard = ({ comment, onVote, onDelete, verifiedOnly = false }) => {
    const navigate = useNavigate();
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [showReplies, setShowReplies] = useState(false);
    const [replies, setReplies] = useState([]);

    // Inline edit state for the top-level comment
    const [commentContent, setCommentContent] = useState(comment.content);
    const [isEditing, setIsEditing] = useState(false);
    const [editDraft, setEditDraft] = useState(comment.content);
    const [savingEdit, setSavingEdit] = useState(false);
    const [editError, setEditError] = useState(null);

    // Inline edit state for replies (one at a time)
    const [editingReplyId, setEditingReplyId] = useState(null);
    const [replyDraft, setReplyDraft] = useState("");
    const [savingReplyEdit, setSavingReplyEdit] = useState(false);
    const [replyEditError, setReplyEditError] = useState(null);

    const editRef = useRef(null);
    const replyEditRef = useRef(null);

    useEffect(() => {
        setCommentContent(comment.content);
    }, [comment.content]);

    // When entering edit mode, bring the editor into view instead of leaving
    // the user scrolled away from it.
    useEffect(() => {
        if (isEditing && editRef.current) {
            editRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            editRef.current.focus({ preventScroll: true });
        }
    }, [isEditing]);

    useEffect(() => {
        if (editingReplyId && replyEditRef.current) {
            replyEditRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            replyEditRef.current.focus({ preventScroll: true });
        }
    }, [editingReplyId]);

    // from context
    const { userId, dnaUserId } = useUser();

    // ----- owner detection for the top-level comment -----
    const commentOwnerId =
        comment.user_id ??
        comment.userId ??
        extractId(comment.user) ??
        comment.owner_id ??
        comment.author_id ??
        extractId(comment.author) ??
        comment.created_by_id ??
        extractId(comment.created_by) ??
        comment.dna_user_id ??
        null;

    const currentUserIds = [userId, dnaUserId].filter(Boolean).map(String);
    const isCommentOwner =
        commentOwnerId && currentUserIds.includes(String(commentOwnerId));

    const voteDisabled = isCommentOwner;
    const voteTitle = isCommentOwner
        ? "You cannot vote on your own comment."
        : undefined;

    const handleVote = (type) => {
        if (isCommentOwner) return;
        if (!dnaUserId) {
            setAuthModalOpen(true);
            return;
        }
        onVote(comment.id, type);
    };

    const handleCommentDeleted = async (commentId) => {
        if (typeof onDelete === "function") onDelete(commentId);

        try {
            await deleteComment(commentId);
            // Option 1: trigger a re-fetch of all comments
            // Option 2: if parent component passes down an onDelete, call it here
            if (typeof onDelete === "function") onDelete(commentId);
        } catch (err) {
            console.error("Error deleting comment:", err);
        }
    };

    const startEditingComment = () => {
        setEditDraft(commentContent);
        setEditError(null);
        setIsEditing(true);
    };

    const cancelEditingComment = () => {
        setIsEditing(false);
        setEditError(null);
    };

    const handleCommentEdited = async () => {
        const trimmed = editDraft.trim();
        if (!trimmed) {
            setEditError("Comment cannot be empty.");
            return;
        }
        if (trimmed === commentContent) {
            setIsEditing(false);
            return;
        }
        setSavingEdit(true);
        setEditError(null);
        try {
            const updated = await editComment(comment.id, trimmed);
            setCommentContent(updated?.content ?? trimmed);
            setIsEditing(false);
        } catch (err) {
            console.error("Error editing comment:", err);
            setEditError(err.message || "Failed to save changes.");
        } finally {
            setSavingEdit(false);
        }
    };


    useEffect(() => {
        const loadReplies = async () => {
            try {
                const data = await fetchReplies(comment.id, { verifiedOnly });
                setReplies(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error loading replies:", error);
            }
        };
        loadReplies();
    }, [showReplyForm, showReplies, comment.id, verifiedOnly]);

    useEffect(() => {
        if (!dnaUserId && showReplyForm) setShowReplyForm(false);
    }, [dnaUserId, showReplyForm]);

    const getReplyOwnerId = (reply) =>
        reply.user_id ??
        reply.userId ??
        extractId(reply.user) ??
        reply.author_id ??
        extractId(reply.author) ??
        reply.dna_user_id ??
        null;

    const visibleReplies = verifiedOnly
        ? replies.filter(
            (reply) =>
                reply.author_is_verified !== false ||
                (getReplyOwnerId(reply) &&
                    currentUserIds.includes(String(getReplyOwnerId(reply))))
          )
        : replies;

    const handleReplyAdded = (newReply) => {
        setReplies((prev) => [newReply, ...prev]);
        setShowReplies(true);
    };

    const getReplyId = (reply) => reply.id ?? reply.uuid ?? reply.reply_id;

    const handleReplyDeleted = async (replyId) => {
        // console.log("deleting reply", replyId, "from comment", comment.id);
        try {
            await deleteReply(replyId, comment.id);

            setReplies((prev) =>
                prev.filter((r) => getReplyId(r) !== replyId)
            );
        } catch (error) {
            console.error("Error deleting reply:", error);
        }
    };

    const startEditingReply = (reply) => {
        setEditingReplyId(getReplyId(reply));
        setReplyDraft(reply.content);
        setReplyEditError(null);
    };

    const cancelEditingReply = () => {
        setEditingReplyId(null);
        setReplyEditError(null);
    };

    const handleReplyEdited = async (replyId) => {
        const trimmed = replyDraft.trim();
        if (!trimmed) {
            setReplyEditError("Reply cannot be empty.");
            return;
        }
        setSavingReplyEdit(true);
        setReplyEditError(null);
        try {
            const updated = await editReply(replyId, comment.id, trimmed);
            setReplies((prev) =>
                prev.map((r) =>
                    getReplyId(r) === replyId
                        ? { ...r, content: updated?.content ?? trimmed }
                        : r
                )
            );
            setEditingReplyId(null);
        } catch (error) {
            console.error("Error editing reply:", error);
            setReplyEditError(error.message || "Failed to save changes.");
        } finally {
            setSavingReplyEdit(false);
        }
    };

    const VOTE_LABELS = {
        SUPPORT: "Supports",
        OPPOSE: "Opposes",
        NEEDS_REVISIONS: "Support with amendments",
        UNDECIDED: "Undecided",
    };
    const voteLabel = VOTE_LABELS[comment.vote] || "Undecided";

    const VOTE_PILL_CLASSES = {
        SUPPORT: "bg-green-100 text-gray-900",
        OPPOSE: "bg-rose-100 text-gray-900",
        NEEDS_REVISIONS: "bg-amber-100 text-gray-900",
        UNDECIDED: "bg-gray-100 text-gray-900",
    };
    const votePillClass = VOTE_PILL_CLASSES[comment.vote] || "bg-gray-100 text-gray-900";

    const formattedDate = new Date(comment.created_at).toLocaleString();

    return (
        <>
        <div className="w-full rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex gap-3 sm:gap-4">
                {/* Votes column */}
                <div className="flex w-10 shrink-0 flex-col items-center">
                    <button
                        onClick={() => handleVote("LIKE")}
                        disabled={voteDisabled}
                        title={voteTitle}
                        aria-disabled={voteDisabled}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#0f172a] hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-40"
                    >
                        ▲
                    </button>
                    <div className="py-1 text-sm font-semibold text-[#0f172a]">
                        {comment.likes || 0}
                    </div>
                    <button
                        onClick={() => handleVote("DISLIKE")}
                        disabled={voteDisabled}
                        title={voteTitle}
                        aria-disabled={voteDisabled}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#0f172a] hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-40"
                    >
                        ▼
                    </button>
                    <div className="py-1 text-sm font-semibold text-[#0f172a]">
                        {comment.dislikes || 0}
                    </div>
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                    <div className="mb-2 flex items-start justify-between gap-3">
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                            <span className="font-semibold text-[#0f172a]">
                                {comment.anonymous ? "Anonymous" : comment.username}
                            </span>
                            {(comment.state || comment.district_number) && (
                                <span className="text-[#334155]">
                                    ({[comment.state, comment.district_number].filter(Boolean).join("-")})
                                </span>
                            )}
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-[#0f172a] ${votePillClass}`}>
                                {voteLabel}
                            </span>
                            <span className="text-xs text-[#334155]">{formattedDate}</span>
                        </div>

                        {isCommentOwner && !isEditing && (
                            <div className="flex shrink-0 items-center gap-1">
                                <button
                                    onClick={startEditingComment}
                                    title="Edit comment"
                                    aria-label="Edit comment"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#334155] transition hover:bg-slate-100 hover:text-[#0f172a]"
                                >
                                    <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => handleCommentDeleted(comment.id)}
                                    title="Delete comment"
                                    aria-label="Delete comment"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-50 text-rose-700 transition hover:bg-rose-100 hover:text-rose-800"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                    </div>

                    {isEditing ? (
                        <div className="mb-2">
                            <textarea
                                ref={editRef}
                                value={editDraft}
                                onChange={(e) => setEditDraft(e.target.value)}
                                rows={3}
                                className="w-full resize-y rounded-lg border border-gray-300 p-2 text-[0.975rem] leading-6 text-gray-900 focus:outline-none focus:ring-2"
                                style={{ borderColor: theme.primary }}
                            />
                            {editError && (
                                <div className="mt-1 text-xs text-rose-600">{editError}</div>
                            )}
                            <div className="mt-2 flex items-center gap-2">
                                <button
                                    onClick={handleCommentEdited}
                                    disabled={savingEdit}
                                    className="rounded-md px-3 py-1 text-xs font-semibold text-white transition disabled:opacity-60"
                                    style={{ backgroundColor: theme.primary }}
                                >
                                    {savingEdit ? "Saving…" : "Save"}
                                </button>
                                <button
                                    onClick={cancelEditingComment}
                                    disabled={savingEdit}
                                    className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 transition hover:bg-gray-100 disabled:opacity-60"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="mb-3 whitespace-pre-wrap text-[0.975rem] leading-6 text-[#0f172a]">
                            {commentContent}
                        </div>
                    )}

                    {/* Actions row */}
                    <div className="flex flex-wrap items-center gap-3">
                    {dnaUserId && (
                        <button
                        onClick={() => setShowReplyForm((v) => !v)}
                        className="inline-flex items-center rounded-lg border px-3 py-1.5 text-sm font-medium transition hover:opacity-90"
                        style={{ borderColor: theme.primary, color: theme.primary }}
                        >
                        {showReplyForm ? "Cancel" : "Reply"}
                        </button>
                    )}

                    {/* Show/Hide replies */}
                    <button
                        onClick={() => setShowReplies((v) => !v)}
                        className="inline-flex items-center gap-1 text-sm text-[#334155] underline hover:no-underline"
                    >
                        {showReplies ? "Hide replies" : `Show replies (${visibleReplies.length})`}
                        <svg
                        className={`h-4 w-4 transition-transform ${showReplies ? "rotate-180" : ""}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                        >
                        <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                            clipRule="evenodd"
                        />
                        </svg>
                    </button>
                    </div>


                    {/* Reply form */}
                    {showReplyForm && (
                        <div className="mt-2">
                            <ReplyForm
                                commentId={comment.id}
                                onReplyAdded={handleReplyAdded}
                                // onReplyDeleted is handled per-reply below
                                canDelete={true}
                            />
                        </div>
                    )}

                    {/* Replies */}
                    {showReplies && (
                        <div className="mt-3 space-y-3 border-l-2 border-slate-200 pl-3 sm:pl-4">
                            {visibleReplies.length === 0 ? (
                                <div className="text-sm text-gray-900">
                                    {verifiedOnly ? "No verified replies yet." : "No replies yet."}
                                </div>
                            ) : (
                                visibleReplies.map((reply) => {
                                    const replyOwnerId =
                                        reply.user_id ??
                                        reply.userId ??
                                        extractId(reply.user) ??
                                        reply.author_id ??
                                        extractId(reply.author) ??
                                        reply.dna_user_id ??
                                        null;

                                    const isOwner =
                                        replyOwnerId &&
                                        currentUserIds.includes(String(replyOwnerId));

                                    return (
                                        <div
                                            key={reply.id}
                                            className="mr-auto w-full rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    {editingReplyId === getReplyId(reply) ? (
                                                        <div>
                                                            <textarea
                                                                ref={replyEditRef}
                                                                value={replyDraft}
                                                                onChange={(e) => setReplyDraft(e.target.value)}
                                                                rows={2}
                                                                className="w-full resize-y rounded-lg border border-gray-300 p-2 text-sm text-gray-900 focus:outline-none focus:ring-2"
                                                                style={{ borderColor: theme.primary }}
                                                            />
                                                            {replyEditError && (
                                                                <div className="mt-1 text-xs text-rose-600">{replyEditError}</div>
                                                            )}
                                                            <div className="mt-2 flex items-center gap-2">
                                                                <button
                                                                    onClick={() => handleReplyEdited(getReplyId(reply))}
                                                                    disabled={savingReplyEdit}
                                                                    className="rounded-md px-3 py-1 text-xs font-semibold text-white transition disabled:opacity-60"
                                                                    style={{ backgroundColor: theme.primary }}
                                                                >
                                                                    {savingReplyEdit ? "Saving…" : "Save"}
                                                                </button>
                                                                <button
                                                                    onClick={cancelEditingReply}
                                                                    disabled={savingReplyEdit}
                                                                    className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 transition hover:bg-gray-100 disabled:opacity-60"
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <p className="text-sm">
                                                                <span className="font-semibold">
                                                                    {reply.anonymous ? "Anonymous" : reply.username}
                                                                </span>{" "}
                                                                {(reply.state || reply.district_number) && (
                                                                <span className="font-semibold text-gray-500">
                                                                    ({[reply.state, reply.district_number].filter(Boolean).join("-")})
                                                                </span>
                                                                )}
                                                                <span className="text-gray-800">: {reply.content}</span>
                                                            </p>
                                                            <div className="mt-1 text-xs text-gray-900">
                                                                {new Date(reply.created_at).toLocaleString()}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>

                                                {isOwner && editingReplyId !== getReplyId(reply) && (
                                                    <div className="flex shrink-0 items-center gap-1">
                                                        <button
                                                            onClick={() => startEditingReply(reply)}
                                                            title="Edit reply"
                                                            aria-label="Edit reply"
                                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#334155] transition hover:bg-slate-100 hover:text-[#0f172a]"
                                                        >
                                                            <PencilIcon className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleReplyDeleted(getReplyId(reply))}
                                                            title="Delete reply"
                                                            aria-label="Delete reply"
                                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-50 text-rose-700 transition hover:bg-rose-100 hover:text-rose-800"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>

        <AuthRequiredModal
            open={authModalOpen}
            onClose={() => setAuthModalOpen(false)}
            onLogin={() => {
                setAuthModalOpen(false);
                navigate("/auth/signup");
            }}
        />
        </>
    );
};

export default CommentCard;
